import { useState, useMemo, ReactNode } from "react";
import { normalizeForSearch, normalizePhoneForSearch } from "@/lib/clientUtils";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Calendar as CalendarIcon, X, Edit, Phone, Mail, Users, UtensilsCrossed, ChevronDown, Check, Clock, Baby, ArrowRightLeft, StickyNote, MoreVertical, AlertTriangle } from "lucide-react";
import { StrollerIcon } from "@/components/icons/StrollerIcon";
import { format, addDays } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { hasInactiveOrMissingTable, hasCapacityExceeded } from "@/lib/reservationProtectionUtils";
import { useIsMobile } from "@/hooks/use-mobile";

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { QuickTableChangeDialog } from "@/components/QuickTableChangeDialog";

import { Table, CombinedTable, Reservation as TypedReservation, TableAvailability, TableWeekdayAvailability } from "@/types/table";
import { useServiceTimeCompare } from "@/hooks/useServiceTimeCompare";

interface Reservation {
  id: number;
  supabaseId: string;
  time: string;
  name: string;
  persons: number;
  numberOfChildren?: number;
  hasStroller?: boolean;
  durationMinutes?: number;
  tableId: string | undefined;
  combinedTableId: string | undefined;
  status: "planifié" | "arrivé" | "annulé" | "en_attente";
  cancellationReason?: string;
  phone: string;
  email: string | undefined;
  reservationDate: Date;
  createdAt: Date;
  notes?: string;
  internalNotes?: string;
}

interface ReservationSearchProps {
  reservations: Reservation[];
  allTables: Table[];
  combinedTables: CombinedTable[];
  onEdit: (reservation: Reservation) => void;
  onView?: (reservation: Reservation) => void;
  onUpdateStatus: (id: number, status: "arrivé" | "annulé" | "planifié", cancellationReason?: string) => void;
  onQuickTableChange: (id: number, tableId: string | null, combinedTableId: string | null) => void;
  onSwapTables?: (aSupabaseId: string, bSupabaseId: string) => Promise<void> | void;
  onSendEmail: (data: { reservationId: number; reservationUuid: string; emailType: 'cancellation' | 'modification' | 'confirmation'; reservationName: string; reservationEmail: string }) => void;

  sendingEmail: number | null;
  getReservationStatus: (reservation: Reservation) => { type: string; label: string; delay: number; variant: string };
  getLoyaltyBadge: (email: string | undefined) => ReactNode;
  getNoShowBadge: (email: string | undefined) => ReactNode;
  getCustomerNotes: (email: string | undefined) => string | null;
  convertedReservations: TypedReservation[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  reservationSettings?: {
    bookingMode: 'slot' | 'time';
    reservationDurationMinutes: number;
  };
}

export function ReservationSearch({ 
  reservations, allTables, combinedTables, onEdit, onView,
  onUpdateStatus, onQuickTableChange, onSwapTables, onSendEmail, sendingEmail,
  getReservationStatus, getLoyaltyBadge, getNoShowBadge, getCustomerNotes,
  convertedReservations, availabilities, weekdayAvailabilities,
  reservationSettings
}: ReservationSearchProps) {
  const compareServiceTime = useServiceTimeCompare();
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set(["planifié", "arrivé", "annulé"]));
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);

  const [calendarOpen, setCalendarOpen] = useState(false);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const quickDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const date = addDays(today, i);
      let label: string;
      if (i === 0) label = "Aujourd'hui";
      else if (i === 1) label = "Demain";
      else {
        const dayName = format(date, 'EEEE', { locale: fr });
        label = dayName.charAt(0).toUpperCase() + dayName.slice(1);
      }
      return { date, label };
    });
  }, [today]);

  const activeDayOffset = useMemo(() => {
    if (!dateFrom || !dateTo) return -1;
    const fromStr = format(dateFrom, 'yyyy-MM-dd');
    const toStr = format(dateTo, 'yyyy-MM-dd');
    if (fromStr !== toStr) return -1;
    for (let i = 0; i < 7; i++) {
      if (fromStr === format(addDays(today, i), 'yyyy-MM-dd')) return i;
    }
    return -1;
  }, [dateFrom, dateTo, today]);

  const hasCustomDate = useMemo(() => {
    return dateFrom !== undefined && activeDayOffset === -1;
  }, [dateFrom, activeDayOffset]);

  const filteredReservations = useMemo(() => {
    return reservations.filter((reservation) => {
      const matchesSearch =
        searchQuery === "" ||
        normalizeForSearch(reservation.name || '').includes(normalizeForSearch(searchQuery)) ||
        normalizeForSearch(reservation.email || '').includes(normalizeForSearch(searchQuery)) ||
        (() => {
          const phoneTerm = normalizePhoneForSearch(searchQuery);
          return phoneTerm.length > 0 && !!reservation.phone && normalizePhoneForSearch(reservation.phone).includes(phoneTerm);
        })();

      const matchesDateFrom =
        !dateFrom || reservation.reservationDate >= dateFrom;
      const matchesDateTo =
        !dateTo || reservation.reservationDate <= dateTo;

      const matchesStatus = selectedStatuses.has(reservation.status);

      return matchesSearch && matchesDateFrom && matchesDateTo && matchesStatus;
    });
  }, [reservations, searchQuery, dateFrom, dateTo, selectedStatuses]);

  // Identifier les réservations sans table active
  const problematicReservationIds = useMemo(() => {
    return new Set(
      filteredReservations
        .filter(res => hasInactiveOrMissingTable(res, allTables, combinedTables))
        .map(res => res.id)
    );
  }, [filteredReservations, allTables, combinedTables]);

  // Identifier les réservations dépassant la capacité
  const capacityExceededIds = useMemo(() => {
    return new Set(
      filteredReservations
        .filter(res => hasCapacityExceeded(res, allTables, combinedTables))
        .map(res => res.id)
    );
  }, [filteredReservations, allTables, combinedTables]);

  const toggleStatus = (status: string) => {
    setSelectedStatuses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  const clearFilters = () => {
    setSearchQuery("");
    setDateFrom(undefined);
    setDateTo(undefined);
    setSelectedStatuses(new Set(["planifié", "arrivé", "annulé"]));
  };

  const renderStatusBadge = (status: { type: string; delay: number }) => {
    switch (status.type) {
      case "arrived": return <Badge className="bg-green-500 text-white text-[10px]">Arrivé</Badge>;
      case "no-show": return <Badge className="bg-gray-700 text-white text-[10px]">No-show</Badge>;
      case "canceled": return <Badge variant="outline" className="text-[10px] text-muted-foreground border-muted-foreground/30 bg-muted/50">Annulé</Badge>;
      case "on-time": return <Badge className="bg-green-500 text-white text-[10px]">À l'heure</Badge>;
      case "late": return <Badge className="bg-orange-500 text-white text-[10px]">+{status.delay} min</Badge>;
      case "very-late": return <Badge variant="destructive" className="text-[10px]">+{status.delay} min</Badge>;
      case "no-show-probable": return <Badge variant="destructive" className="bg-gray-700 text-[10px]">No-show probable</Badge>;
      default: return <Badge variant="secondary" className="text-[10px]">Planifié</Badge>;
    }
  };

  const getTableName = (reservation: Reservation) => {
    if (reservation.tableId) {
      const table = allTables.find(t => t.id === reservation.tableId);
      return table?.name || "Table supprimée";
    }
    if (reservation.combinedTableId) {
      const ct = combinedTables.find(c => c.id === reservation.combinedTableId);
      return ct?.name || "Table combinée supprimée";
    }
    return "Non assignée";
  };

  return (
    <div className="space-y-6">
      {/* Search Filters */}
      <Card className="p-6">
        <div className="space-y-4">
          <h3 className="text-lg font-serif font-semibold mb-4">Rechercher une réservation</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search Input */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Nom, email, téléphone..."
                  value={searchQuery}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSearchQuery(val);
                    if (val.trim() !== "") {
                      setDateFrom(undefined);
                      setDateTo(undefined);
                    }
                  }}
                  className="pl-10"
                />
              </div>
            </div>

          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {quickDays.map((day, i) => (
              <Button
                key={i}
                variant={activeDayOffset === i ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  if (activeDayOffset === i) {
                    setDateFrom(undefined);
                    setDateTo(undefined);
                  } else {
                    setDateFrom(day.date);
                    setDateTo(day.date);
                  }
                }}
              >
                {day.label}
              </Button>
            ))}

            {/* Calendar dropdown for other dates */}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={hasCustomDate ? "default" : "outline"}
                  size="sm"
                  className="gap-1"
                >
                  <CalendarIcon className="w-4 h-4" />
                  {hasCustomDate && dateFrom
                    ? format(dateFrom, "dd/MM/yyyy", { locale: fr })
                    : "Autre date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateFrom}
                  onSelect={(date) => {
                    if (date) {
                      date.setHours(0, 0, 0, 0);
                      setDateFrom(date);
                      setDateTo(date);
                    }
                    setCalendarOpen(false);
                  }}
                  initialFocus
                  locale={fr}
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>

            {(dateFrom || dateTo) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDateFrom(undefined);
                  setDateTo(undefined);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            )}

            {/* Status Filter - Multiple Selection */}
            <Popover open={statusPopoverOpen} onOpenChange={setStatusPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[250px] justify-between"
                >
                  <span className="truncate">
                    {selectedStatuses.size === 3
                      ? "Tous les statuts"
                      : selectedStatuses.size === 0
                      ? "Aucun statut"
                      : Array.from(selectedStatuses)
                          .map(s => s.charAt(0).toUpperCase() + s.slice(1))
                          .join(", ")}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[250px] p-4 bg-popover z-50" align="start">
                <div className="space-y-3">
                  <div className="font-medium text-sm mb-2">Filtrer par statut</div>
                  <div className="space-y-2">
                    {[
                      { value: "planifié", label: "Planifié" },
                      { value: "arrivé", label: "Arrivé" },
                      { value: "annulé", label: "Annulé" }
                    ].map(({ value, label }) => (
                      <div key={value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`status-${value}`}
                          checked={selectedStatuses.has(value)}
                          onCheckedChange={() => toggleStatus(value)}
                        />
                        <label
                          htmlFor={`status-${value}`}
                          className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {label}
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="pt-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        if (selectedStatuses.size === 3) {
                          setSelectedStatuses(new Set());
                        } else {
                          setSelectedStatuses(new Set(["planifié", "arrivé", "annulé"]));
                        }
                      }}
                    >
                      {selectedStatuses.size === 3 ? "Tout désélectionner" : "Tout sélectionner"}
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Clear Filters */}
            {(searchQuery || dateFrom || dateTo || selectedStatuses.size !== 3) && (
              <Button variant="ghost" onClick={clearFilters}>
                <X className="w-4 h-4 mr-2" />
                Effacer les filtres
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {filteredReservations.length} résultat{filteredReservations.length > 1 ? "s" : ""} trouvé{filteredReservations.length > 1 ? "s" : ""}
        </p>
      </div>

      {/* Results List */}
      {filteredReservations.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">Aucune réservation trouvée</p>
        </Card>
      ) : isMobile ? (
        /* Mobile compact view — mirrors DashboardListView "Par jour" */
        (() => {
          const statusBarColor = (reservation: Reservation): string => {
            const s = getReservationStatus(reservation);
            switch (s.type) {
              case "arrived":
              case "on-time": return "bg-emerald-500";
              case "late": return "bg-orange-500";
              case "very-late":
              case "no-show-probable": return "bg-red-500";
              case "canceled": return "bg-slate-300 dark:bg-slate-600";
              case "no-show": return "bg-gray-700";
              default: return "bg-slate-300 dark:bg-slate-600";
            }
          };

          const isTimeMode = reservationSettings?.bookingMode === 'time';
          const reservationDurationMinutes = reservationSettings?.reservationDurationMinutes ?? 120;
          const calculateEndTime = (time: string, duration: number) => {
            const [h, m] = time.split(':').map(Number);
            const total = h * 60 + m + duration;
            return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
          };

          // Sort then group by date -> by HH:MM
          const sorted = [...filteredReservations].sort((a, b) => {
            const da = a.reservationDate.getTime();
            const db = b.reservationDate.getTime();
            if (da !== db) return da - db;
            return compareServiceTime(a.time, b.time);
          });

          type TimeGroup = { time: string; items: Reservation[] };
          type DateGroup = { dateKey: string; date: Date; times: TimeGroup[]; totalRes: number; totalPers: number };
          const dateGroups: DateGroup[] = [];
          sorted.forEach((r) => {
            const dateKey = format(r.reservationDate, 'yyyy-MM-dd');
            let dg = dateGroups[dateGroups.length - 1];
            if (!dg || dg.dateKey !== dateKey) {
              dg = { dateKey, date: r.reservationDate, times: [], totalRes: 0, totalPers: 0 };
              dateGroups.push(dg);
            }
            const lastTime = dg.times[dg.times.length - 1];
            if (lastTime && lastTime.time === r.time) lastTime.items.push(r);
            else dg.times.push({ time: r.time, items: [r] });
            dg.totalRes += 1;
            dg.totalPers += r.persons;
          });

          return (
            <div className="rounded-lg border bg-card overflow-clip">
              {dateGroups.map((dg) => {
                const dateLabel = format(dg.date, "EEE d MMM yyyy", { locale: fr });
                return (
                  <div key={dg.dateKey}>
                    <div className="sticky top-0 z-20 bg-muted px-3 py-1.5 border-b text-xs font-semibold flex items-center justify-between">
                      <span className="capitalize">{dateLabel}</span>
                      <span className="text-muted-foreground font-normal">
                        {dg.totalRes} résa · {dg.totalPers} pers.
                      </span>
                    </div>
                    {dg.times.map((group) => (
                      <div key={`${dg.dateKey}-${group.time}`}>
                        <div className="bg-muted/70 backdrop-blur px-3 py-1 border-b text-[11px] uppercase tracking-wide text-muted-foreground font-medium flex items-center justify-between">
                          <span className="tabular-nums">{group.time}</span>
                          <span>
                            {group.items.length} résa · {group.items.reduce((s, r) => s + r.persons, 0)} pers.
                          </span>
                        </div>
                        {group.items.map((reservation) => {
                          const customerNotes = getCustomerNotes(reservation.email);
                          const duration = reservation.durationMinutes ?? reservationDurationMinutes;
                          const isCustomDuration = reservation.durationMinutes != null && reservation.durationMinutes !== reservationDurationMinutes;
                          const endTime = calculateEndTime(reservation.time, duration);
                          const tableLabel = getTableName(reservation);
                          const isCanceled = reservation.status === "annulé";
                          const isArrived = reservation.status === "arrivé";
                          const status = getReservationStatus(reservation);
                          const hasTableProblem = problematicReservationIds.has(reservation.id);
                          const hasCapacityProblem = !hasTableProblem && capacityExceededIds.has(reservation.id);

                          return (
                            <div
                              key={reservation.id}
                              className={cn(
                                "relative flex items-stretch border-b last:border-b-0 transition-colors",
                                isArrived && "opacity-60",
                                isCanceled && "opacity-50",
                                hasTableProblem && !isArrived && "bg-amber-50 dark:bg-amber-950/20",
                                hasCapacityProblem && !isArrived && "bg-blue-50 dark:bg-blue-950/20"
                              )}
                            >
                              <div aria-hidden="true" className={cn("w-[3px] shrink-0", statusBarColor(reservation))} />

                              <div className="flex items-center pl-2.5 shrink-0">
                                <span className="text-sm font-semibold tabular-nums w-[44px] text-foreground">
                                  {reservation.time}
                                </span>
                              </div>

                              <div className="flex items-center pl-1 pr-2 shrink-0">
                                <div className="h-7 min-w-7 px-1.5 rounded-full bg-muted flex items-center justify-center text-xs font-semibold tabular-nums">
                                  {reservation.persons}
                                  {(reservation.numberOfChildren ?? 0) > 0 && (
                                    <span className="text-[10px] text-muted-foreground ml-0.5">+{Math.min(reservation.numberOfChildren!, 9)}{(reservation.numberOfChildren ?? 0) > 9 ? '+' : ''}</span>
                                  )}
                                </div>
                              </div>

                              <div className="flex-1 min-w-0 py-2 pr-1">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <span className={cn("text-sm font-medium truncate min-w-0", isCanceled && "line-through")}>
                                    {reservation.name.split(" ")[0]}
                                  </span>
                                  <span className="text-muted-foreground text-xs shrink-0">·</span>
                                  <span
                                    className={cn(
                                      "text-xs truncate min-w-0",
                                      hasTableProblem ? "text-amber-700 dark:text-amber-400 font-medium" :
                                      hasCapacityProblem ? "text-blue-700 dark:text-blue-400 font-medium" :
                                      "text-muted-foreground"
                                    )}
                                  >
                                    {tableLabel}
                                  </span>
                                </div>
                                {(isTimeMode || isCustomDuration) && (
                                  <div className="text-[10px] text-muted-foreground tabular-nums mt-0.5">
                                    → {endTime}{isCustomDuration && <span className="ml-1">({duration}′)</span>}
                                  </div>
                                )}
                              </div>

                              <div className="flex items-center gap-1 pr-1.5 shrink-0">
                                {getLoyaltyBadge(reservation.email)}
                                {getNoShowBadge(reservation.email)}
                                {reservation.hasStroller && <StrollerIcon size={12} className="text-muted-foreground" />}
                                {hasTableProblem && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
                                {reservation.notes && <span aria-label="Note spéciale" className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                                {customerNotes && <StickyNote aria-label="Note client" className="w-3.5 h-3.5 text-violet-500" />}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-9 w-9 -mr-1"
                                      aria-label={`Actions pour ${reservation.name} à ${reservation.time}`}
                                    >
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-52 bg-background z-[60]">
                                    {status.type !== "arrived" && status.type !== "canceled" && status.type !== "no-show" && (
                                      <DropdownMenuItem onClick={() => onUpdateStatus(reservation.id, "arrivé")}>
                                        <Check className="w-4 h-4 mr-2" /> Marquer arrivé
                                      </DropdownMenuItem>
                                    )}
                                    {status.type !== "canceled" && status.type !== "no-show" && (
                                      <DropdownMenuItem onClick={() => onUpdateStatus(reservation.id, "annulé")}>
                                        <X className="w-4 h-4 mr-2" /> Annuler
                                      </DropdownMenuItem>
                                    )}
                                    {(status.type === "arrived" || status.type === "canceled" || status.type === "no-show") && (
                                      <DropdownMenuItem onClick={() => onUpdateStatus(reservation.id, "planifié")}>
                                        <Clock className="w-4 h-4 mr-2" /> Replanifier
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => onEdit(reservation)}>
                                      <Edit className="w-4 h-4 mr-2" /> Modifier
                                    </DropdownMenuItem>
                                    {reservation.phone && (
                                      <DropdownMenuItem onClick={() => window.location.href = `tel:${reservation.phone}`}>
                                        <Phone className="w-4 h-4 mr-2" /> Appeler
                                      </DropdownMenuItem>
                                    )}
                                    {reservation.email && reservation.status === "annulé" && (
                                      <DropdownMenuItem onClick={() => onSendEmail({ reservationId: reservation.id, reservationUuid: reservation.supabaseId, emailType: 'cancellation', reservationName: reservation.name, reservationEmail: reservation.email! })}>
                                        <Mail className="w-4 h-4 mr-2" /> Email d'annulation
                                      </DropdownMenuItem>
                                    )}
                                    {reservation.email && reservation.status === "planifié" && (
                                      <>
                                        <DropdownMenuItem onClick={() => onSendEmail({ reservationId: reservation.id, reservationUuid: reservation.supabaseId, emailType: 'confirmation', reservationName: reservation.name, reservationEmail: reservation.email! })}>
                                          <Mail className="w-4 h-4 mr-2" /> Email de confirmation
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => onSendEmail({ reservationId: reservation.id, reservationUuid: reservation.supabaseId, emailType: 'modification', reservationName: reservation.name, reservationEmail: reservation.email! })}>
                                          <Mail className="w-4 h-4 mr-2" /> Email de modification
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                    {reservation.email && reservation.status === "arrivé" && (
                                      <DropdownMenuItem onClick={() => onSendEmail({ reservationId: reservation.id, reservationUuid: reservation.supabaseId, emailType: 'modification', reservationName: reservation.name, reservationEmail: reservation.email! })}>
                                        <Mail className="w-4 h-4 mr-2" /> Email de modification
                                      </DropdownMenuItem>
                                    )}
                                    <QuickTableChangeDialog
                                      reservation={{
                                        id: reservation.id,
                                        supabaseId: reservation.supabaseId,
                                        tableId: reservation.tableId,
                                        combinedTableId: reservation.combinedTableId,
                                        persons: reservation.persons,
                                        time: reservation.time,
                                        reservationDate: reservation.reservationDate,
                                        customerName: reservation.name,
                                      }}
                                      tables={allTables}
                                      combinedTables={combinedTables}
                                      allReservations={convertedReservations}
                                      availabilities={availabilities}
                                      weekdayAvailabilities={weekdayAvailabilities}
                                      onTableChange={onQuickTableChange}
                                      onSwapTables={onSwapTables}

                                      disabled={reservation.status === 'annulé'}
                                      reservationSettings={reservationSettings}
                                    >
                                      <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={reservation.status === 'annulé'}>
                                        <ArrowRightLeft className="w-4 h-4 mr-2" /> Changer de table
                                      </DropdownMenuItem>
                                    </QuickTableChangeDialog>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })()
      ) : (
        <div className="space-y-1">
          {filteredReservations.map((reservation) => {
            const status = getReservationStatus(reservation);
            const customerNotes = getCustomerNotes(reservation.email);
            const tableName = getTableName(reservation);
            const hasNotes = reservation.notes || reservation.internalNotes || customerNotes;

            return (
              <Card 
                key={reservation.id} 
                className={cn(
                  "hover:shadow-sm transition-shadow overflow-hidden",
                  problematicReservationIds.has(reservation.id) && 
                  "border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/20",
                  !problematicReservationIds.has(reservation.id) && 
                  capacityExceededIds.has(reservation.id) && 
                  "border-2 border-blue-500 bg-blue-50 dark:bg-blue-950/20"
                )}
              >
                {/* Main row */}
                <div className="flex items-center gap-2 px-3 py-2">
                  <div className="flex-shrink-0">
                    {renderStatusBadge(status)}
                  </div>

                  <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                    {format(reservation.reservationDate, "dd/MM")} {reservation.time}
                  </span>

                  <div className="flex items-center gap-1.5 min-w-0 flex-shrink">
                    <span className="font-semibold text-sm truncate">{reservation.name}</span>
                    {getLoyaltyBadge(reservation.email)}
                    {getNoShowBadge(reservation.email)}
                    {problematicReservationIds.has(reservation.id) && (
                      <Badge variant="outline" className="bg-amber-500 text-white border-amber-600 text-[10px] flex-shrink-0">
                        ⚠️ Table
                      </Badge>
                    )}
                    {!problematicReservationIds.has(reservation.id) && 
                     capacityExceededIds.has(reservation.id) && (
                      <Badge variant="outline" className="bg-blue-500 text-white border-blue-600 text-[10px] flex-shrink-0">
                        👥 Capacité
                      </Badge>
                    )}
                  </div>

                  <span className="text-xs text-muted-foreground flex items-center gap-0.5 whitespace-nowrap flex-shrink-0">
                    <Users className="w-3 h-3" />
                    {reservation.persons}
                    {(reservation.numberOfChildren ?? 0) > 0 && (
                      <span className="flex items-center gap-0.5">
                        <Baby className="w-3 h-3" />+{reservation.numberOfChildren}
                      </span>
                    )}
                    {reservation.hasStroller && (
                      <StrollerIcon size={12} className="text-muted-foreground" />
                    )}
                  </span>

                  <span className="text-xs text-muted-foreground truncate hidden sm:inline flex-shrink">
                    {tableName}
                  </span>

                  {reservation.phone && (
                    <a href={`tel:${reservation.phone}`} className="text-xs text-muted-foreground whitespace-nowrap hidden md:inline flex-shrink-0 hover:text-foreground">
                      <Phone className="w-3 h-3 inline mr-0.5" />{reservation.phone}
                    </a>
                  )}

                  <div className="flex-1" />

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {status.type !== "arrived" && status.type !== "canceled" && status.type !== "no-show" && (
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onUpdateStatus(reservation.id, "arrivé")}>
                        <Check className="w-3 h-3" /> Arrivé
                      </Button>
                    )}
                    {status.type !== "canceled" && status.type !== "no-show" && (
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onUpdateStatus(reservation.id, "annulé")}>
                        <X className="w-3 h-3" /> Annuler
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onEdit(reservation)}>
                      <Edit className="w-3 h-3" /> Modifier
                    </Button>
                    {(status.type === "arrived" || status.type === "canceled" || status.type === "no-show") && (
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onUpdateStatus(reservation.id, "planifié")}>
                        <Clock className="w-3 h-3" /> Replanifier
                      </Button>
                    )}

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48 bg-background z-[60]">
                        {reservation.phone && (
                          <DropdownMenuItem onClick={() => window.location.href = `tel:${reservation.phone}`}>
                            <Phone className="w-3.5 h-3.5 mr-2" /> Appeler
                          </DropdownMenuItem>
                        )}
                        {reservation.email && reservation.status === "annulé" && (
                          <DropdownMenuItem onClick={() => onSendEmail({ reservationId: reservation.id, reservationUuid: reservation.supabaseId, emailType: 'cancellation', reservationName: reservation.name, reservationEmail: reservation.email! })}>
                            <Mail className="w-3.5 h-3.5 mr-2" /> Email d'annulation
                          </DropdownMenuItem>
                        )}
                        {reservation.email && reservation.status === "planifié" && (
                          <>
                            <DropdownMenuItem onClick={() => onSendEmail({ reservationId: reservation.id, reservationUuid: reservation.supabaseId, emailType: 'confirmation', reservationName: reservation.name, reservationEmail: reservation.email! })}>
                              <Mail className="w-3.5 h-3.5 mr-2" /> Email de confirmation
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onSendEmail({ reservationId: reservation.id, reservationUuid: reservation.supabaseId, emailType: 'modification', reservationName: reservation.name, reservationEmail: reservation.email! })}>
                              <Mail className="w-3.5 h-3.5 mr-2" /> Email de modification
                            </DropdownMenuItem>
                          </>
                        )}
                        {reservation.email && reservation.status === "arrivé" && (
                          <DropdownMenuItem onClick={() => onSendEmail({ reservationId: reservation.id, reservationUuid: reservation.supabaseId, emailType: 'modification', reservationName: reservation.name, reservationEmail: reservation.email! })}>
                            <Mail className="w-3.5 h-3.5 mr-2" /> Email de modification
                          </DropdownMenuItem>
                        )}
                        <QuickTableChangeDialog
                          reservation={{
                            id: reservation.id,
                            supabaseId: reservation.supabaseId,
                            tableId: reservation.tableId,
                            combinedTableId: reservation.combinedTableId,
                            persons: reservation.persons,
                            time: reservation.time,
                            reservationDate: reservation.reservationDate,
                            customerName: reservation.name,
                          }}
                          tables={allTables}
                          combinedTables={combinedTables}
                          allReservations={convertedReservations}
                          availabilities={availabilities}
                          weekdayAvailabilities={weekdayAvailabilities}
                          onTableChange={onQuickTableChange}
                          onSwapTables={onSwapTables}

                          disabled={reservation.status === 'annulé'}
                          reservationSettings={reservationSettings}
                        >
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={reservation.status === 'annulé'}>
                            <ArrowRightLeft className="w-3.5 h-3.5 mr-2" /> Changer de table
                          </DropdownMenuItem>
                        </QuickTableChangeDialog>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {hasNotes && (
                  <div className="px-3 pb-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs italic">
                    {reservation.notes && (
                      <span className="text-amber-600 dark:text-amber-400">{reservation.notes}</span>
                    )}
                    {reservation.internalNotes && (
                      <span className="text-violet-600 dark:text-violet-400 flex items-center gap-0.5">
                        <StickyNote className="w-3 h-3 flex-shrink-0" />{reservation.internalNotes}
                      </span>
                    )}
                    {customerNotes && (
                      <span className="text-violet-500 flex items-center gap-0.5">
                        <StickyNote className="w-3 h-3 flex-shrink-0" />{customerNotes}
                      </span>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
